import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import { debounce } from "lodash";

import BaseTextField from "../BaseTextField";
import { fetchCitiesPaginated } from "../../../services/api/country";
import {
  brandFontFamily,
  brandFontSize,
  brandInputTextColor,
} from "../../../utils/variables";

const PAGE_SIZE = 20;

// Mirrors BaseAutoSelect's styled Autocomplete so the city field lines up with
// the other selects (compact input height + orange focus border).
const autoSelectSx = {
  "& .MuiInputBase-input": {
    color: brandInputTextColor,
    fontFamily: brandFontFamily,
    fontSize: brandFontSize,
    fontWeight: 300,
    height: "12px !important",
    padding: "6px 0",
  },
  "& .MuiInputBase-root": {
    maxHeight: "auto !important",
    height: "auto !important",
    padding: "0px 0",
  },
  "& .MuiIconButton-root": {
    backgroundColor: "#fff !important",
    borderLeft: "none !important",
    padding: "0 !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1.5px solid orange",
    boxShadow: "none",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "orange",
  },
};

/**
 * City dropdown backed by our paginated /countries/by-name/:name/cities API.
 * Loads 20 cities at a time, fetches the next page on scroll, and searches
 * server-side (debounced) as the user types — so it never loads a country's
 * full (10k+) city list.
 *
 * Props:
 *  - country:  country *name* used to scope the query (the dropdown is disabled
 *              until a country is selected)
 *  - value:    selected city string
 *  - onChange: (cityString) => void  ("" when cleared)
 */
const BaseAsyncCitySelect = ({
  country,
  value,
  label = "City",
  placeholder = "Select City",
  required = false,
  labelShrink = false,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryRef = useRef(""); // active search term
  const requestRef = useRef(0); // guards against out-of-order responses

  const loadPage = useCallback(
    async (pageToLoad, q, append) => {
      if (!country) {
        setOptions([]);
        setHasNextPage(false);
        return;
      }
      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const { data, meta } = await fetchCitiesPaginated({
          country,
          page: pageToLoad,
          limit: PAGE_SIZE,
          q,
        });
        if (requestId !== requestRef.current) return; // a newer request superseded this one
        setOptions((prev) => (append ? [...prev, ...data] : data));
        setHasNextPage(Boolean(meta?.hasNextPage));
        setPage(pageToLoad);
      } catch (error) {
        if (requestId === requestRef.current) {
          if (!append) setOptions([]);
          setHasNextPage(false);
        }
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [country],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((q) => {
        queryRef.current = q;
        loadPage(1, q, false);
      }, 300),
    [loadPage],
  );

  // Reset and reload whenever the selected country changes.
  useEffect(() => {
    queryRef.current = "";
    setOptions([]);
    setHasNextPage(false);
    setPage(1);
    if (country) loadPage(1, "", false);
    return () => debouncedSearch.cancel();
  }, [country, loadPage, debouncedSearch]);

  const handleScroll = (event) => {
    const node = event.currentTarget;
    const reachedBottom =
      node.scrollTop + node.clientHeight >= node.scrollHeight - 8;
    if (reachedBottom && hasNextPage && !loading) {
      loadPage(page + 1, queryRef.current, true);
    }
  };

  const handleInputChange = (event, newInput, reason) => {
    if (reason === "input") debouncedSearch(newInput);
    else if (reason === "clear") debouncedSearch("");
  };

  // MUI warns when the controlled value isn't present in `options` — keep it in.
  const mergedOptions =
    value && !options.includes(value) ? [value, ...options] : options;

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
        if (country && options.length === 0) {
          loadPage(1, queryRef.current, false);
        }
      }}
      onClose={() => setOpen(false)}
      disabled={!country}
      options={mergedOptions}
      value={value || null}
      onChange={(event, newValue) => onChange?.(newValue || "")}
      onInputChange={handleInputChange}
      filterOptions={(opts) => opts} // search happens server-side; don't filter locally
      loading={loading}
      fullWidth
      sx={autoSelectSx}
      ListboxProps={{ onScroll: handleScroll }}
      // Same option row as BaseAutoSelect so the city list matches the country list.
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <MenuItem
            {...optionProps}
            key={option}
            value={option}
            style={{ padding: 8, display: "flex", alignItems: "center" }}
          >
            <p
              style={{
                fontSize: `${brandFontSize} !important`,
                fontFamily: brandFontFamily,
                color: `${brandInputTextColor} !important`,
                fontWeight: 300,
              }}
            >
              {option}
            </p>
          </MenuItem>
        );
      }}
      renderInput={(params) => (
        <BaseTextField
          {...params}
          isDefaultField={false}
          labelShrink={labelShrink}
          optional={false}
          required={required}
          label={label}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            // Chrome ignores autoComplete="off" on address-like fields and shows
            // its saved-addresses popup over the option list.
            autoComplete: "new-password",
            // Validation is handled by formik/yup - keep the browser's native
            // "Please fill out this field" bubble away from the dropdown.
            required: false,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default BaseAsyncCitySelect;
